import type { Grenade, StratColor, StratItem, StratWidth } from "@69ly/shared";
import { GRENADE_ICONS } from "@69ly/ui/grenade-icons";

/**
 * Drawing a strat board's items as SVG, in a 1000 × 1000 box over the radar.
 * Colours are written on the elements themselves (not CSS classes), so the same
 * SVG can be turned into the PNG export as it is.
 */

export const STRAT_COLOR: Record<StratColor, string> = {
  white: "#e8ecef",
  yellow: "#ffe066",
  orange: "#ff9f43",
  red: "#ff5c5c",
  blue: "#5ab0ff",
  green: "#6fd08c",
};
export const SIDE_COLOR = { t: "#f4b860", ct: "#5ab0ff" } as const;
export const GRENADE_COLOR: Record<Grenade, string> = { smoke: "#c9d4de", flash: "#ffe066", molotov: "#ff7a45", he: "#6fd08c" };
const INK = "#0d1013";
const ACCENT = "#5ab0ff";

export const S = 1000;

/** Stroke thickness of arrows and strokes, in the 1000 × 1000 box. */
export const STRAT_WIDTH: Record<StratWidth, number> = { thin: 2, normal: 3.5, bold: 6 };
/** Boards saved before widths existed draw as normal. */
export const widthOf = (item: { width?: StratWidth }): StratWidth => item.width ?? "normal";

/** A player on the board: the disc's radius, and how far the facing triangle reaches. */
export const TOKEN_R = 12;
const TIP = TOKEN_R + 16;
/** The wedge in front of a player: a tip ahead, a base wider than a line, under the disc. */
function facing(x: number, y: number, angle: number): string {
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const bx = x + ux * (TOKEN_R - 3);
  const by = y + uy * (TOKEN_R - 3);
  const half = 10;
  return [
    [x + ux * TIP, y + uy * TIP],
    [bx - uy * half, by + ux * half],
    [bx + uy * half, by - ux * half],
  ]
    .map((p) => p.map((n) => n.toFixed(1)).join(","))
    .join(" ");
}

/** Where a player with no aim yet shows the handle to drag. */
export const AIM_DEFAULT = 0.05;
const pt = (p: [number, number]) => `${(p[0] * S).toFixed(1)},${(p[1] * S).toFixed(1)}`;

function ArrowHead({ from, to, color, w }: { from: [number, number]; to: [number, number]; color: string; w: number }) {
  const ax = from[0] * S;
  const ay = from[1] * S;
  const bx = to[0] * S;
  const by = to[1] * S;
  const len = Math.hypot(bx - ax, by - ay) || 1;
  const ux = (bx - ax) / len;
  const uy = (by - ay) / len;
  const L = 12 + w * 3.2;
  const W = L / 2;
  const p1 = `${bx},${by}`;
  const p2 = `${bx - ux * L - uy * W},${by - uy * L + ux * W}`;
  const p3 = `${bx - ux * L + uy * W},${by - uy * L - ux * W}`;
  return <polygon points={`${p1} ${p2} ${p3}`} fill={color} stroke={INK} strokeWidth={1.5} strokeLinejoin="round" />;
}

export function GrenadeIcon({ grenade, x, y, size }: { grenade: Grenade; x: number; y: number; size: number }) {
  const icon = GRENADE_ICONS[grenade];
  return (
    <svg
      x={x - size / 2}
      y={y - size / 2}
      width={size}
      height={size}
      viewBox={icon.viewBox}
      color={GRENADE_COLOR[grenade]}
      fill={GRENADE_COLOR[grenade]}
      // Static, bundled SVG paths: no user content.
      dangerouslySetInnerHTML={{ __html: icon.body }}
    />
  );
}

/** Where an item is, for its selection ring and its popover: its centre, or for lines their end. */
export function itemAnchor(item: StratItem): [number, number] {
  switch (item.kind) {
    case "arrow":
      return item.to;
    case "pen":
      return item.points[item.points.length - 1];
    default:
      return [item.u, item.v];
  }
}

export function StratItemSvg({
  item,
  selected = false,
  linked = false,
  showView = true,
  aim = false,
  k = 1,
}: {
  item: StratItem;
  selected?: boolean;
  linked?: boolean;
  /** Draw players' view lines (the board can hide them all). */
  showView?: boolean;
  /** Show the handle that sets where a player looks. */
  aim?: boolean;
  /**
   * How much to shrink players and grenade markers around their spot: one over
   * the zoom, so they keep their size on screen like the markers of the other
   * maps while the drawings (arrows, strokes, text) grow with the map.
   */
  k?: number;
}) {
  const ring = selected ? ACCENT : undefined;
  // Around a point: scaled by k, the point staying put. Marked so the picture export can undo it.
  const around = (x: number, y: number) => (k !== 1 ? { transform: `translate(${x} ${y}) scale(${k}) translate(${-x} ${-y})`, "data-zoomfix": "" } : {});
  switch (item.kind) {
    case "token": {
      const x = item.u * S;
      const y = item.v * S;
      const side = SIDE_COLOR[item.side];
      const look = item.look ?? null;
      const angle = look ? Math.atan2(look[1] * S - y, look[0] * S - x) : null;
      const at = (r: number, a: number): [number, number] => [x + Math.cos(a) * r, y + Math.sin(a) * r];
      const lookAt: [number, number] | null = look ? [look[0] * S, look[1] * S] : null;
      const far = lookAt ? Math.hypot(lookAt[0] - x, lookAt[1] - y) : 0;
      const handle: [number, number] = lookAt ?? [x + AIM_DEFAULT * S, y];
      return (
        <g data-id={item.id} className="strat-item">
          {showView && angle !== null && item.view !== false && far > TIP * k + 4 && (
            <g pointerEvents="none" className="strat-view">
              <line x1={at(TIP * k, angle)[0]} y1={at(TIP * k, angle)[1]} x2={lookAt![0]} y2={lookAt![1]} stroke={INK} strokeWidth={4 * k} strokeLinecap="round" opacity={0.4} />
              <line x1={at(TIP * k, angle)[0]} y1={at(TIP * k, angle)[1]} x2={lookAt![0]} y2={lookAt![1]} stroke={side} strokeWidth={2 * k} strokeDasharray={`${9 * k} ${6 * k}`} strokeLinecap="round" opacity={0.9} />
              <circle cx={lookAt![0]} cy={lookAt![1]} r={3.5 * k} fill={side} stroke={INK} strokeWidth={1.5 * k} />
            </g>
          )}
          <g {...around(x, y)}>
            {ring && <circle cx={x} cy={y} r={TOKEN_R + 7} fill="none" stroke={ring} strokeWidth={2.5} strokeDasharray="5 4" />}
            {angle !== null && (
              <polygon
                className="strat-facing"
                points={facing(x, y, angle)}
                fill={side}
                stroke={INK}
                strokeWidth={2}
                strokeLinejoin="round"
              />
            )}
            <circle cx={x} cy={y} r={TOKEN_R} fill={side} stroke={INK} strokeWidth={2.5} />
            <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight={800} fontFamily="Barlow, Arial, sans-serif" fill="#0b0f14">
              {item.n}
            </text>
          </g>
          {aim && (
            <g className="strat-aim" data-look={item.id}>
              {!lookAt && <line x1={x + TOKEN_R * k} y1={y} x2={handle[0]} y2={handle[1]} stroke={ACCENT} strokeWidth={1.5 * k} strokeDasharray={`${3 * k} ${3 * k}`} pointerEvents="none" />}
              <circle cx={handle[0]} cy={handle[1]} r={14 * k} fill="transparent" />
              <circle cx={handle[0]} cy={handle[1]} r={6.5 * k} fill={lookAt ? ACCENT : INK} stroke={lookAt ? INK : ACCENT} strokeWidth={2 * k} />
            </g>
          )}
        </g>
      );
    }
    case "arrow": {
      const c = STRAT_COLOR[item.color];
      const w = STRAT_WIDTH[widthOf(item)];
      const [x1, y1, x2, y2] = [item.from[0] * S, item.from[1] * S, item.to[0] * S, item.to[1] * S];
      // The line stops inside the head, so a thick one does not poke out of its tip.
      const len = Math.hypot(x2 - x1, y2 - y1) || 1;
      const back = Math.min(len, (12 + w * 3.2) * 0.6);
      const ex = x2 - ((x2 - x1) / len) * back;
      const ey = y2 - ((y2 - y1) / len) * back;
      return (
        <g data-id={item.id} className="strat-item">
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={22} strokeLinecap="round" />
          {ring && <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={ring} strokeWidth={w + 9} strokeLinecap="round" opacity={0.45} />}
          <line x1={x1} y1={y1} x2={ex} y2={ey} stroke={INK} strokeWidth={w + 3} strokeLinecap="round" opacity={0.55} />
          <line x1={x1} y1={y1} x2={ex} y2={ey} stroke={c} strokeWidth={w} strokeLinecap="round" />
          <ArrowHead from={item.from} to={item.to} color={c} w={w} />
        </g>
      );
    }
    case "pen": {
      const points = item.points.map(pt).join(" ");
      const c = STRAT_COLOR[item.color];
      const w = STRAT_WIDTH[widthOf(item)];
      return (
        <g data-id={item.id} className="strat-item">
          <polyline points={points} fill="none" stroke="transparent" strokeWidth={20} strokeLinecap="round" strokeLinejoin="round" />
          {ring && <polyline points={points} fill="none" stroke={ring} strokeWidth={w + 9} strokeLinecap="round" strokeLinejoin="round" opacity={0.45} />}
          <polyline points={points} fill="none" stroke={INK} strokeWidth={w + 3} strokeLinecap="round" strokeLinejoin="round" opacity={0.5} />
          <polyline points={points} fill="none" stroke={c} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    }
    case "text": {
      const x = item.u * S;
      const y = item.v * S;
      return (
        <g data-id={item.id} className="strat-item">
          {ring && <rect x={x - item.text.length * 7.5 - 10} y={y - 22} width={item.text.length * 15 + 20} height={44} rx={8} fill="none" stroke={ring} strokeWidth={3} strokeDasharray="6 5" />}
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={26}
            fontWeight={700}
            fontFamily="Barlow, Arial, sans-serif"
            fill={STRAT_COLOR[item.color]}
            stroke={INK}
            strokeWidth={6}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {item.text}
          </text>
        </g>
      );
    }
    case "grenade": {
      const x = item.u * S;
      const y = item.v * S;
      const c = GRENADE_COLOR[item.grenade];
      return (
        <g data-id={item.id} className="strat-item" {...around(x, y)}>
          {ring && <circle cx={x} cy={y} r={29} fill="none" stroke={ring} strokeWidth={3} strokeDasharray="6 5" />}
          <circle cx={x} cy={y} r={20} fill="rgba(13,16,19,0.88)" stroke={c} strokeWidth={linked ? 3.5 : 2} strokeDasharray={linked ? undefined : "4 4"} />
          <GrenadeIcon grenade={item.grenade} x={x} y={y} size={30} />
          {linked && <circle cx={x + 15} cy={y - 15} r={6} fill={c} stroke={INK} strokeWidth={2} />}
        </g>
      );
    }
  }
}

/** The arrow or stroke being drawn, before it becomes an item. */
export function DraftSvg({
  draft,
  color,
  width,
}: {
  draft: { kind: "arrow"; from: [number, number]; to: [number, number] } | { kind: "pen"; points: [number, number][] };
  color: StratColor;
  width: StratWidth;
}) {
  const c = STRAT_COLOR[color];
  const w = STRAT_WIDTH[width];
  if (draft.kind === "arrow")
    return (
      <g opacity={0.85}>
        <line x1={draft.from[0] * S} y1={draft.from[1] * S} x2={draft.to[0] * S} y2={draft.to[1] * S} stroke={c} strokeWidth={w} strokeLinecap="round" />
        <ArrowHead from={draft.from} to={draft.to} color={c} w={w} />
      </g>
    );
  return <polyline points={draft.points.map(pt).join(" ")} fill="none" stroke={c} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />;
}

/**
 * The board as a PNG: the radar, then the drawings (the SVG as it is on screen),
 * and a line naming the board and the phase.
 */
export async function exportBoardPng(svg: SVGSVGElement, radarSrc: string, caption: string, fileName: string): Promise<void> {
  const size = 1400;
  const footer = 56;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size + footer;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No canvas");
  ctx.fillStyle = "#0d1013";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const load = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Could not load ${src}`));
      img.src = src;
    });

  ctx.drawImage(await load(radarSrc), 0, 0, size, size);

  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  copy.setAttribute("width", String(size));
  copy.setAttribute("height", String(size));
  copy.removeAttribute("class");
  copy.removeAttribute("style");
  // The aim handle is for editing, not for the picture; players and markers at their own size, whatever the zoom.
  copy.querySelectorAll(".strat-aim").forEach((n) => n.remove());
  copy.querySelectorAll("[data-zoomfix]").forEach((n) => n.removeAttribute("transform"));
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copy)], { type: "image/svg+xml" }));
  try {
    ctx.drawImage(await load(url), 0, 0, size, size);
  } finally {
    URL.revokeObjectURL(url);
  }

  ctx.fillStyle = "#e8ecef";
  ctx.font = "600 26px Barlow, Arial, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(caption, 24, size + footer / 2);
  ctx.fillStyle = "#9aa7b4";
  ctx.textAlign = "right";
  ctx.fillText("69LY Companion", size - 24, size + footer / 2);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("No image");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
